import styled from 'styled-components';
import {
  ButtonPrimary,
  semanticBackgroundColors,
  spacer20,
  spacer64,
} from '@spotify-internal/encore-web';
import { EntityBannerMedium } from '@spotify-internal/encore-web/content-platform/components/EntityBannerMedium';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { useClipboard } from '@hooks/useClipboard';
import { buildAddSnackbarAction } from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { isEmptyString } from '@helpers/utils';

const EntityBannerContainer = styled.div`
  margin-top: ${spacer64};
  margin-bottom: ${spacer64};
`;

const StyledButtonPrimary = styled(ButtonPrimary)`
  margin-top: ${spacer20};
`;

interface EntityHeaderProps {
  artistName: string;
  artistUri: string;
  albumsLength: string;
  avatarUri: string;
  isVerified: boolean;
}

export const EntityHeader = ({
  artistName,
  artistUri,
  albumsLength,
  avatarUri,
  isVerified,
}: EntityHeaderProps) => {
  const t = useT();
  const { copyText } = useClipboard(
    typeof artistUri === 'string' ? artistUri : '',
  );

  const [, dispatch] = useSnackbarNotifications();

  const handleClickCopy = async (eventLabel: string) => {
    sendEvent({
      eventCategory: EVENT_CATEGORY.ARTIST_ENTITY_HEADER,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel,
      product: GA_PRODUCT_CATALOG,
    });

    const didCopy = await copyText();

    if (didCopy) {
      dispatch(
        buildAddSnackbarAction(
          t(
            'atlas.snackbars.copied_to_clipboard',
            'Copied to clipboard',
            'Snackbar message to indicate copied to clipboard',
          ),
        ),
      );
    }
  };

  const albumsLengthString = albumsLength.toString();

  return (
    <EntityBannerContainer>
      <EntityBannerMedium
        semanticBackgroundColor={semanticBackgroundColors.backgroundBase}
        condensed
        entityType="Artist"
        entityLabel={t(
          'atlas.breadcrumbs.artist',
          'Artist',
          'Text for a breadcrumb link. Refers to "Artist" in the noun form',
        )}
        entityTitle={artistName}
        isArtistVerified={isVerified}
        imageUrl={
          !isEmptyString(avatarUri)
            ? avatarUri
            : 'https://atlas.spotifycdn.com/missing_artist_profile_image.png'
        }
        entitySubtitle={
          albumsLengthString === '1'
            ? t(
                'atlas.count.release',
                '1 release',
                'Singular form of release i.e. 1 release',
              )
            : t(
                'atlas.count.releases',
                `{albumsLengthString} releases`,
                'Plural form of release i.e. 2 releases, 3 releases',
                {
                  albumsLengthString,
                },
              )
        }
        actions={
          <StyledButtonPrimary
            buttonSize="sm"
            aria-labelledby="CopyURI"
            onClick={() => handleClickCopy('URI copied')}
          >
            {t(
              'atlas.actions.copy_spotify_uri',
              'Copy Spotify URI',
              'Text for Copy Spotify URI action',
            )}
          </StyledButtonPrimary>
        }
      />
    </EntityBannerContainer>
  );
};
